/**
 * Google's website translate widget, driven from the language dialog's chips
 * and its "Another language" select.
 *
 * WHY THE WIDGET AND NOT TRANSLATIONS OF OUR OWN. A translation is copy, and
 * machine translating the client's sentences and shipping them as their site
 * would put words on the page that nobody there has read, in a language nobody
 * there can check. The notice the header carries ("Translated by Google
 * Translate.") is what makes the widget
 * honest. It is the free element.js widget: no key, no account. Google has
 * stopped promoting it and it still works; if it ever stops, that notice and
 * this file are the two places that change together.
 *
 * The chips are the shortlist for the remembered council (the Site Settings
 * list when there is none), re-rendered on a council change. The select holds
 * every census language that is not a chip; one with a code switches the page
 * the same way a chip does, one without is listed but cannot be chosen.
 * includedLanguages is the union of every code the site knows, so a chip from
 * another council's list or a select option still works after the widget has
 * loaded; the widget's own menu is hidden, so the union is never shown.
 * Codes are Google's own, which are not all ISO: Mandarin is zh-CN, and plain
 * zh gets you nothing.
 */

import { current as currentCouncil, onChange as onCouncilChange } from './council.js';

// Written right to left. Punjabi is not: Gurmukhi runs left to right, and
// assuming otherwise because it sits beside Arabic in a list is the mistake
// this set exists to stop. Persian, Urdu and Hebrew are.
const RTL = new Set(['ar', 'fa', 'ur', 'iw', 'he']);

const COOKIE = 'googtrans';
const SCRIPT_ID = 'google-translate-script';
const HOST_ID = 'google_translate_host';

let busy = false;

const container = () => document.querySelector('[data-awdd-language-chips]');
const chips = () => Array.from(document.querySelectorAll('[data-awdd-language-chips] [data-awdd-language]'));
const beyondSelect = () => document.querySelector('select[data-awdd-language-census]');

// Rows of name and code from a JSON attribute, or nothing.
const rowsFrom = (json) => {
	try {
		const rows = JSON.parse(json || '[]');
		return Array.isArray(rows) ? rows.filter((r) => r && typeof r.name === 'string' && r.name) : [];
	} catch {
		return [];
	}
};

// The Site Settings shortlist (without English), printed on the container.
const fallbackRows = () => rowsFrom(container().dataset.awddLanguageFallback);
const councilRows = () => (Array.isArray(window.awddCouncils) ? window.awddCouncils : []).flatMap((c) => (Array.isArray(c.languages) ? c.languages : []));
// Every census language, printed on the select.
const censusRows = () => {
	const select = beyondSelect();
	return select ? rowsFrom(select.dataset.awddLanguageCensus) : [];
};

// Every name and code the site knows, so a label can be found for a cookie
// set under another council's list or from the select.
const allRows = () => [{ name: 'English', code: 'en' }, ...fallbackRows(), ...councilRows(), ...censusRows()];
const allCodes = () => Array.from(new Set(allRows().map((r) => r.code).filter((c) => c && c !== 'en')));
const nameForCode = (code) => {
	const row = allRows().find((r) => r.code === code);
	return row ? row.name : 'English';
};

// English, then the council's list, else the shortlist: the static build's languagesFor().
const listFor = (council) => {
	const own = council && Array.isArray(council.languages) && council.languages.length ? council.languages : fallbackRows();
	return [{ name: 'English', code: 'en' }, ...own.filter((r) => r.name && r.code !== 'en' && r.name !== 'English')];
};

// The "Another language" select: every census language that is not a chip,
// in the census order, as the static build's languagesBeyond(). The server
// prints the same options for its own chips, so JS off is unchanged. An
// option's value is its Google code; a language without one is disabled.
const rebuildBeyond = (rows) => {
	const select = beyondSelect();
	if (!select) return;
	const census = censusRows();
	if (!census.length) return;
	const taken = new Set(rows.map((r) => r.name));
	const placeholder = select.options[0] || null;
	select.textContent = '';
	if (placeholder) select.appendChild(placeholder);
	census.forEach((r) => {
		if (taken.has(r.name)) return;
		const o = document.createElement('option');
		o.value = r.code || '';
		o.textContent = r.name;
		if (!r.code) o.disabled = true;
		select.appendChild(o);
	});
};

const render = (rows) => {
	const box = container();
	if (!box) return;
	box.textContent = '';
	rows.forEach((r) => {
		const b = document.createElement('button');
		b.type = 'button';
		b.className = 'chip notranslate';
		b.setAttribute('aria-pressed', 'false');
		b.dataset.awddLanguage = r.code || '';
		b.dataset.awddLanguageName = r.name;
		b.textContent = r.name;
		box.appendChild(b);
	});
	rebuildBeyond(rows);
};

// The widget stores the choice in a cookie shaped /en/<code> and reads it on
// load. It writes it on the bare host and, for a real domain, on the dotted
// one; both are set so a reload keeps the language whichever comes back first.
const readCookie = () => {
	const m = String(document.cookie).match(/(?:^|;\s*)googtrans=([^;]+)/);
	if (!m) return 'en';
	const parts = decodeURIComponent(m[1]).split('/');
	return parts[parts.length - 1] || 'en';
};

// Parent domain suffixes of the host, longest first, down to two labels:
// www.example.com.au gives .example.com.au and .com.au (a browser refuses the
// public suffix, which does no harm). Single-label and IP hosts have none.
// Clearing these stops a cookie the widget set on a parent domain from
// translating the page again after a reload back to English.
const parentDomains = () => {
	const host = window.location.hostname;
	if (!host || host.indexOf('.') === -1 || /^[\d.]+$/.test(host) || host.indexOf(':') !== -1) return [];
	const labels = host.split('.');
	const out = [];
	for (let i = 1; i <= labels.length - 2; i++) out.push(`.${labels.slice(i).join('.')}`);
	return out;
};

const writeCookie = (code) => {
	const value = code === 'en' ? '' : `/en/${code}`;
	const hosts = [null, window.location.hostname, `.${window.location.hostname}`];
	if (!value) hosts.push(...parentDomains());
	for (const host of hosts) {
		const domain = host ? `;domain=${host}` : '';
		document.cookie = value
			? `${COOKIE}=${value};path=/${domain}`
			: `${COOKIE}=;path=/${domain};expires=Thu, 01 Jan 1970 00:00:00 GMT`;
	}
};

const currentLanguage = () => {
	try {
		return readCookie();
	} catch {
		return 'en';
	}
};

// Right to left is a document-level setting, so it is set here: the header, the
// footer and every panel have to turn together. lang moves with it, because a
// screen reader picking the wrong voice is the same bug as the wrong direction.
const applyDirection = (code) => {
	const el = document.documentElement;
	el.setAttribute('dir', RTL.has(code) ? 'rtl' : 'ltr');
	el.setAttribute('lang', code === 'en' ? 'en-AU' : code);
};

// The select, once it has something in it. Everything waits on this, not on the element.
const combo = () => document.querySelector('select.goog-te-combo');
const isReady = () => {
	const el = combo();
	return !!el && el.options.length > 1;
};

let loading = null;

// Load the widget once and resolve when its select is ready.
//
// READY MEANS POPULATED, NOT PRESENT. The widget draws an empty select as soon
// as the script runs, then fills it from a second request. Dispatching a change
// at a select with no options sets nothing and fires nothing: no error anywhere
// to explain it. So the wait is for an option to exist.
const loadTranslate = () => {
	if (loading) return loading;
	loading = new Promise((resolve) => {
		if (!document.getElementById(HOST_ID)) {
			const host = document.createElement('div');
			host.id = HOST_ID;
			// The host is clipped, not display none and NOT pushed off screen with
			// a negative left. Hiding the host itself with display none can stop
			// the widget drawing at all, so the host has to render. And a negative
			// left overflows the other way in RTL: the document went 11439px wide
			// and Arabic rendered as a blank page. The clip pattern takes no space
			// in either direction. What this does not cover: the gadget div the
			// widget draws inside the host carries the skiptranslate class, which
			// app.css sets to display none, so the select itself sits in a display
			// none subtree. That is fine because the select is only read and driven
			// from script (options, value, a dispatched change), never measured or
			// focused.
			host.setAttribute('aria-hidden', 'true');
			host.style.cssText =
				'position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap';
			document.body.appendChild(host);
		}

		window.googleTranslateElementInit = () => {
			try {
				// eslint-disable-next-line no-undef
				new google.translate.TranslateElement(
					{
						pageLanguage: 'en',
						includedLanguages: allCodes().join(','),
						autoDisplay: false,
					},
					HOST_ID,
				);
			} catch {
				// Offline, blocked, or the endpoint has changed. The site stays the
				// English site, which is the correct thing to be when translation
				// is unavailable.
			}
		};

		if (!document.getElementById(SCRIPT_ID)) {
			const s = document.createElement('script');
			s.id = SCRIPT_ID;
			s.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
			s.async = true;
			s.onerror = () => resolve(false);
			document.head.appendChild(s);
		}

		// Poll for the select rather than racing the callback: the callback fires
		// when the script runs, the select appears when the widget has drawn.
		// Ten seconds, not six: the script, its runtime and the language list are
		// three round trips, and a slow network is not a developer laptop.
		const started = performance.now();
		const tick = () => {
			if (isReady()) return resolve(true);
			if (performance.now() - started > 10000) return resolve(false);
			window.setTimeout(tick, 120);
		};
		tick();
	});
	return loading;
};

// Switch the page.
//
// The select is driven rather than the cookie alone, because the cookie only
// takes effect on a reload and this page never reloads. The cookie is written
// as well, so a reload or deep link comes back in the same language. Back to
// English is the same path with the code 'en'.
const setLanguage = async (code) => {
	const wasTranslated = currentLanguage() !== 'en';
	const live = combo();
	if (code === 'en') {
		if (wasTranslated || (live && live.value)) {
			// The free widget does not restore the original text in place reliably.
			writeCookie('en');
			applyDirection('en');
			window.location.reload();
		}
		// Otherwise nothing is translated: nothing to undo, and no reason to
		// pull Google's script in for it.
		return true;
	}
	const ready = await loadTranslate();
	writeCookie(code);
	applyDirection(code);
	const select = combo();
	if (!ready || !select) {
		// The cookie was written before the widget was known to work. Put it
		// back, or a reload comes back half switched.
		writeCookie('en');
		applyDirection('en');
		return false;
	}
	// The option has to be there as well as the select. A missing one is a
	// mistake in the chip codes, not a timing problem, so it reads as a failure.
	const wanted = code === 'en' ? '' : code;
	if (wanted && ![...select.options].some((o) => o.value === wanted)) {
		writeCookie('en');
		applyDirection('en');
		return false;
	}
	select.value = wanted;
	// bubbles, because the widget has listened on an ancestor in some versions.
	select.dispatchEvent(new Event('change', { bubbles: true }));
	// Announced, so anything showing the current language follows without being
	// handed a setter.
	window.dispatchEvent(new CustomEvent('language-change', { detail: { code, name: nameForCode(code) } }));
	return true;
};

// The header label, the pressed chip and the select follow the current code.
// The select shows the language only when it is one of its own options, so a
// chip language leaves it on its placeholder.
const reflect = (code) => {
	const name = nameForCode(code);
	document.querySelectorAll('[data-awdd-language-label]').forEach((el) => { el.textContent = name; });
	chips().forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.awddLanguage === code)));
	const select = beyondSelect();
	if (select) select.value = [...select.options].some((o) => o.value === code && o.value) ? code : '';
};

// One switch at a time, from a chip or the select. The controls are marked
// aria-disabled, not disabled: a disabled button drops focus to the body,
// which takes the dialog's Escape and Tab trap with it.
const choose = async (code, pending) => {
	if (busy) return;
	busy = true;
	const all = chips();
	const select = beyondSelect();
	all.forEach((c) => c.setAttribute('aria-disabled', 'true'));
	if (select) select.setAttribute('aria-disabled', 'true');
	if (pending) pending.start();
	let ok = false;
	try {
		ok = await setLanguage(code);
	} finally {
		if (pending) pending.end();
		all.forEach((c) => c.removeAttribute('aria-disabled'));
		if (select) select.removeAttribute('aria-disabled');
		busy = false;
	}
	// Only claim the switch if it happened: a blocked request leaves the
	// English page, and a chip saying Arabic over English text is worse.
	if (ok) reflect(code);
	else reflect(currentLanguage() === code ? 'en' : currentLanguage());
};

export default function initTranslate() {
	const box = container();
	if (!box) return;

	// The chips follow the remembered council, as the static dialog does.
	render(listFor(currentCouncil()));
	onCouncilChange((council) => {
		render(listFor(council));
		reflect(currentLanguage());
	});

	// Called once on load, so a language chosen before a refresh is still on and
	// the direction matches it. The widget restores the text from the cookie; it
	// does not set dir or lang.
	const code = currentLanguage();
	reflect(code);
	if (code !== 'en') {
		applyDirection(code);
		loadTranslate();
	}

	// One listener on the container: the chips are re-rendered on a council change.
	box.addEventListener('click', (e) => {
		const chip = e.target.closest('[data-awdd-language]');
		if (!chip || !box.contains(chip)) return;
		// No code: a language the widget cannot translate. The chip shows, as in
		// the static build, and does nothing.
		if (!chip.dataset.awddLanguage) return;
		if (busy) return;
		choose(chip.dataset.awddLanguage, {
			start: () => { chip.textContent = `${chip.dataset.awddLanguageName}...`; },
			end: () => { chip.textContent = chip.dataset.awddLanguageName; },
		});
	});

	// The select: its value is the code, and the placeholder does nothing.
	const select = beyondSelect();
	if (select) {
		select.addEventListener('change', () => {
			const wanted = select.value;
			if (!wanted) return;
			if (busy) {
				reflect(currentLanguage());
				return;
			}
			choose(wanted, null);
		});
	}
}
